<template>
  <div class="subject-selector">
    <div class="search-subjects mb-4">
      <v-radio-group
        v-model="selectedScheme"
        row
        dense
        class="mb-2 mt-0"
      >
        <v-radio
          v-for="scheme in schemeOptions"
          :key="scheme.value"
          :label="scheme.label"
          :value="scheme.value"
        />
      </v-radio-group>

      <v-autocomplete
        v-model="selectedSubject"
        :items="subjectResults"
        :loading="isSearching"
        :search-input.sync="subjectSearch"
        hide-no-data
        hide-selected
        item-text="heading"
        item-value="id"
        :label="$t('admin.productsDatabase.importWizard.steps.publicationDetails.searchSubjects')"
        :placeholder="$t('admin.productsDatabase.importWizard.steps.publicationDetails.searchSubjectsPlaceholder')"
        prepend-icon="mdi-magnify"
        return-object
        clearable
        @change="addSubject"
      >
        <template #item="{ item }">
          <v-list-item-content>
            <v-list-item-title v-text="item.heading" />
            <v-list-item-subtitle>
              ID: <strong>{{ item.code }}</strong> &nbsp;&nbsp;|&nbsp;&nbsp;
              Scheme: <span class="text--primary">{{ item.subjectScheme.heading }}</span>
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </div>

    <v-divider />

    <div class="selected-subjects mt-4">
      <h3 class="subtitle-2 mb-2">
        {{ $t('admin.productsDatabase.importWizard.steps.publicationDetails.selectedSubjects') }}
      </h3>

      <v-alert
        v-if="!selectedSubjects.length"
        type="info"
        text
        class="my-3"
      >
        {{ $t('admin.productsDatabase.importWizard.steps.publicationDetails.noSubjectsSelected') }}
      </v-alert>

      <v-chip-group
        column
      >
        <v-chip
          v-for="subject in selectedSubjects"
          :key="subject.id"
          close
          color="primary"
          text-color="white"
          @click:close="removeSubject(subject)"
        >
          {{ subject.heading }}
          <span class="mx-1 font-weight-light">({{ subject.subjectScheme.heading }})</span>
        </v-chip>
      </v-chip-group>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import SubjectsQueries from '@/graphql/SubjectsQueries';
import SubjectsMutations from '@/graphql/SubjectsMutations';
import IRIS, { createIri } from '@/iris';

// ONIX List 27: 93 = Thema subject category
// Custom eKitabu schemes: 1001 = Kenya CBC, 1002 = Kenya Education Levels, 1003 = eKitabu subjects
const SCHEME_IDS = Object.freeze({
  THEMA: '93',
  KENYA_CBC: '1001',
  KENYA_LEVELS: '1002',
  EKITABU: '1003',
});

export default {
  name: 'SubjectSelector',
  props: {
    bookId: {
      type: String,
      required: true
    },
    initialSubjects: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      subjectSearch: '',
      isSearching: false,
      subjectResults: [],
      selectedSubject: null,
      selectedSubjects: [],
      selectedScheme: null,
      schemeOptions: [
        { label: this.$t('common.all'), value: null },
        { label: 'Thema', value: SCHEME_IDS.THEMA },
        { label: 'Kenya CBC', value: SCHEME_IDS.KENYA_CBC },
        { label: this.$t('admin.productsDatabase.importWizard.steps.publicationDetails.schemeKenyaLevels'), value: SCHEME_IDS.KENYA_LEVELS },
        { label: 'eKitabu', value: SCHEME_IDS.EKITABU },
      ],
      searchDebounced: null
    };
  },
  watch: {
    subjectSearch(val) {
      if (val) {
        this.isSearching = true;
        this.searchDebounced(val);
      } else {
        this.subjectResults = [];
      }
    },
    selectedScheme() {
      if (this.subjectSearch) {
        this.isSearching = true;
        this.searchDebounced(this.subjectSearch);
      }
    },
    initialSubjects: {
      handler(newVal) {
        if (newVal && newVal.length) {
          this.loadExistingSubjects();
        }
      },
      deep: true
    }
  },
  created() {
    this.searchDebounced = debounce(this.performSubjectSearch, 500);
    this.loadExistingSubjects();
  },
  methods: {
    loadExistingSubjects() {
      if (this.initialSubjects && this.initialSubjects.length) {
        if (this.initialSubjects[0].node) {
          this.selectedSubjects = this.initialSubjects.map(edge => edge.node);
        } else {
          this.selectedSubjects = [...this.initialSubjects];
        }
      } else {
        this.selectedSubjects = [];
      }
    },

    showError(text) {
      this.$store.dispatch('snackbar/showSnackbar', { text, color: 'error' });
    },

    async performSubjectSearch(searchTerm) {
      try {
        const variables = {
          search: searchTerm,
          first: 20
        };
        if (this.selectedScheme) {
          variables.schemeId = this.selectedScheme;
        }

        const result = await this.$apollo.query({
          query: SubjectsQueries.searchSubjects,
          variables
        });

        this.subjectResults = result.data.subjects.edges.map(edge => edge.node);
      } catch (error) {
        console.error('Error searching subjects:', error);
        this.showError(this.$t('admin.productsDatabase.importWizard.steps.publicationDetails.subjectSearchError'));
      } finally {
        this.isSearching = false;
      }
    },

    async addSubject(subject) {
      if (!subject) return;

      const alreadySelected = this.selectedSubjects.some(s => s.id === subject.id);
      if (alreadySelected) {
        this.selectedSubject = null;
        this.subjectSearch = '';
        return;
      }

      try {
        if (!this.bookId) {
          throw new Error('Book ID not found');
        }

        const result = await this.$apollo.mutate({
          mutation: SubjectsMutations.addSubjectToBook,
          variables: {
            input: {
              id: createIri(IRIS.localBook, this.bookId),
              subjectId: subject.id
            }
          }
        });

        if (result.data && result.data.addSubjectLocalBook && result.data.addSubjectLocalBook.localBook) {
          if (result.data.addSubjectLocalBook.localBook.subjects) {
            const updatedSubjects = result.data.addSubjectLocalBook.localBook.subjects.edges.map(edge => edge.node);
            this.selectedSubjects = updatedSubjects;
            this.$emit('subjects-updated', this.selectedSubjects);
          } else {
            this.selectedSubjects.push(subject);
            this.$emit('subjects-updated', this.selectedSubjects);
          }
        }
      } catch (error) {
        console.error('Error adding subject:', error);
        this.showError(this.$t('admin.productsDatabase.importWizard.steps.publicationDetails.subjectAddError'));
      } finally {
        this.selectedSubject = null;
        this.subjectSearch = '';
      }
    },

    async removeSubject(subject) {
      try {
        if (!this.bookId) {
          throw new Error('Book ID not found');
        }

        const result = await this.$apollo.mutate({
          mutation: SubjectsMutations.removeSubjectFromBook,
          variables: {
            input: {
              id: createIri(IRIS.localBook, this.bookId),
              subjectId: subject.id
            }
          }
        });

        if (result.data && result.data.removeSubjectLocalBook && result.data.removeSubjectLocalBook.localBook) {
          if (result.data.removeSubjectLocalBook.localBook.subjects) {
            const updatedSubjects = result.data.removeSubjectLocalBook.localBook.subjects.edges.map(edge => edge.node);
            this.selectedSubjects = updatedSubjects;
            this.$emit('subjects-updated', this.selectedSubjects);
          } else {
            this.selectedSubjects = this.selectedSubjects.filter(s => s.id !== subject.id);
            this.$emit('subjects-updated', this.selectedSubjects);
          }
        }
      } catch (error) {
        console.error('Error removing subject:', error);
        this.showError(this.$t('admin.productsDatabase.importWizard.steps.publicationDetails.subjectRemoveError'));
      }
    }
  }
};
</script>
